import { useId, useRef, useState } from 'react';

export interface DropZoneProps {
  /** What to drop, said as the zone's heading: "Drop a confirmation PDF here". */
  label: string;
  /** The button's words: "Choose a PDF". */
  choose: string;
  hint: string;
  /** What the file picker offers: `application/pdf`. */
  accept: string;
  /** Several files at once, as a booking's invoice and its boarding pass. */
  multiple?: boolean;
  /** The first file, and those with it when several are taken. */
  onFile: (file: File, more: File[]) => void;
}

/**
 * A file dropped on the zone, or chosen with its button (docs/02 §6): the native file input is
 * hidden and only opens the picker, so the zone looks and speaks the same in every browser, and
 * the keyboard reaches it through the button.
 */
export function DropZone({
  label,
  choose,
  hint,
  accept,
  multiple = false,
  onFile,
}: DropZoneProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  return (
    <section
      className={over ? 'drop-zone drop-zone-over' : 'drop-zone'}
      aria-labelledby={`${id}-label`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => {
        setOver(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const [file, ...more] = [...event.dataTransfer.files];
        if (file) onFile(file, multiple ? more : []);
      }}
    >
      <p className="drop-zone-label" id={`${id}-label`}>
        {label}
      </p>
      <p className="quiet" id={`${id}-hint`}>
        {hint}
      </p>
      <button
        type="button"
        className="button"
        aria-describedby={`${id}-hint`}
        onClick={() => {
          input.current?.click();
        }}
      >
        {choose}
      </button>
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        tabIndex={-1}
        aria-hidden="true"
        data-testid="drop-zone-input"
        onChange={(event) => {
          const [file, ...more] = [...(event.target.files ?? [])];
          if (file) onFile(file, more);
          event.target.value = '';
        }}
      />
    </section>
  );
}
